import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getAuth } from "firebase/auth";
import { doc, getFirestore, serverTimestamp, updateDoc } from "firebase/firestore";

import { app } from "@/config/firebaseConfig";
import { track, LogEvents } from "@/analytics/track";
import type { AppLanguage } from "@/i18n";

/**
 * Switches the UI language and (when signed in) persists the choice to the
 * user's `BusinessMember` doc so it follows them across devices. The local
 * choice is cached to localStorage by the i18next language detector, so it
 * also works on unauthenticated screens (sign-in, onboarding).
 */
export function useChangeLanguage() {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage ?? i18n.language) as AppLanguage;

  const changeLanguage = useCallback(
    async (next: AppLanguage) => {
      if (next === language) return;

      // Apply immediately; the detector caches the choice to localStorage.
      await i18n.changeLanguage(next);

      track({
        event: LogEvents.LanguageChanged.name,
        channel: LogEvents.LanguageChanged.channel,
        language: next,
      });

      const user = getAuth(app).currentUser;
      if (!user) return;

      try {
        await updateDoc(doc(getFirestore(app), "BusinessMember", user.uid), {
          language: next,
          updatedAt: serverTimestamp(),
        });
      } catch (err) {
        // Non-fatal: the local choice still applies and will re-sync on the
        // next change. We don't want a failed write to block the UI switch.
        console.error("Failed to persist language preference", err);
      }
    },
    [i18n, language],
  );

  return { language, changeLanguage };
}
